// Drag & drop plumbing shared by the Calendar view and the Planner: a DndContext that
// understands day cells, hour columns and the "unschedule" drop zone.

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { addDays, differenceInCalendarDays } from 'date-fns';
import { getDb, useDb } from '@/lib/store';
import { statusOf } from '@/lib/hierarchy';
import { StatusIcon } from '@/components/fields/Status';
import { HOUR_H, applyDrop, dayOf, isTimedTask, taskSpan, timedDuration } from './calendarUtils';

/** data attached to draggable chips / rows */
export interface CalDragData {
  taskId: string;
  /** for day-spanning bars: first day of the rendered segment and its length, to find the grabbed day */
  segStart?: number;
  segDays?: number;
  /** timed block in the hour grid: keep the grab offset so the block's top lands where it is shown */
  timed?: boolean;
}

/** data attached to drop zones */
export type CalDropData =
  | { kind: 'day'; day: number; allDay?: boolean }
  | { kind: 'time'; day: number; el: RefObject<HTMLElement>; inside?: (x: number, y: number) => boolean }
  | { kind: 'unschedule' };

export interface DropPreview {
  day: number;
  minutes: number;
  duration: number;
}

interface CalDndState {
  activeTaskId: string | null;
  preview: DropPreview | null;
  /** days the dragged task would cover if dropped on the hovered day cell */
  dayRange: { start: number; end: number } | null;
  /** true right after a drag ended — swallow the click the browser fires on the common ancestor */
  suppressClick: () => boolean;
}

const Ctx = createContext<CalDndState>({ activeTaskId: null, preview: null, dayRange: null, suppressClick: () => false });

export function useCalDnd() {
  return useContext(Ctx);
}

const SNAP_MIN = 15;

const collision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  if (!hits.length) return hits;
  const p = args.pointerCoordinates;
  const byId = new Map(args.droppableContainers.map((c) => [c.id, c.data.current as CalDropData | undefined]));
  const rank = (d: CalDropData | undefined) => (d?.kind === 'unschedule' ? 0 : d?.kind === 'day' ? 1 : 2);
  return hits
    .filter((h) => {
      const d = byId.get(h.id);
      // hour columns are taller than their scroller: ignore the parts scrolled out of view
      return !(d?.kind === 'time' && d.inside && p && !d.inside(p.x, p.y));
    })
    .sort((a, b) => rank(byId.get(a.id)) - rank(byId.get(b.id)));
};

export function CalendarDnd({ children }: { children: ReactNode }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [preview, setPreview] = useState<DropPreview | null>(null);
  const [dayRange, setDayRange] = useState<{ start: number; end: number } | null>(null);
  const activeId = useRef<string | null>(null);
  const pointer = useRef({ x: 0, y: 0 });
  const anchor = useRef<number | null>(null);
  const grabOffsetY = useRef(0);
  const duration = useRef(30);
  const endedAt = useRef(0);
  const onPointerMove = useRef((e: PointerEvent) => {
    pointer.current = { x: e.clientX, y: e.clientY };
  });

  const minutesAt = (d: Extract<CalDropData, { kind: 'time' }>): number | null => {
    const el = d.el.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const y = pointer.current.y - grabOffsetY.current - r.top;
    const m = Math.round(((y / HOUR_H) * 60) / SNAP_MIN) * SNAP_MIN;
    return Math.max(0, Math.min(24 * 60 - SNAP_MIN, m));
  };

  const stop = () => {
    window.removeEventListener('pointermove', onPointerMove.current, true);
    endedAt.current = Date.now();
    activeId.current = null;
    setActiveTaskId(null);
    setPreview(null);
    setDayRange(null);
  };

  const onDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as CalDragData | undefined;
    if (!data) return;
    const ev = e.activatorEvent as PointerEvent;
    pointer.current = { x: ev.clientX, y: ev.clientY };
    window.addEventListener('pointermove', onPointerMove.current, true);
    // dnd-kit measures the active rect after this callback; read the grabbed element directly
    const grabbed = ev.target instanceof Element ? ev.target.closest('.cal-chip, .cal-timed, .cal-row') : null;
    const rect = grabbed?.getBoundingClientRect() ?? e.active.rect.current.initial;
    anchor.current = null;
    if (data.segStart !== undefined && data.segDays && rect) {
      const idx = Math.max(0, Math.min(data.segDays - 1, Math.floor((ev.clientX - rect.left) / (rect.width / data.segDays))));
      anchor.current = addDays(data.segStart, idx).getTime();
    }
    grabOffsetY.current = data.timed && rect ? Math.max(0, ev.clientY - rect.top) : 0;
    activeId.current = data.taskId;
    setActiveTaskId(data.taskId);
  };

  const onDragMove = (e: DragMoveEvent) => {
    const d = e.over?.data.current as CalDropData | undefined;
    if (d?.kind === 'day') {
      const range = projectedRange(activeId.current, anchor.current, d.day);
      setDayRange((r) => (r && range && r.start === range.start && r.end === range.end ? r : range));
    } else setDayRange((r) => (r ? null : r));
    if (d?.kind !== 'time') {
      setPreview((p) => (p ? null : p));
      return;
    }
    const m = minutesAt(d);
    if (m === null) return;
    setPreview((p) => (p && p.day === d.day && p.minutes === m ? p : { day: d.day, minutes: m, duration: duration.current }));
  };

  const onDragEnd = (e: DragEndEvent) => {
    const data = e.active.data.current as CalDragData | undefined;
    const d = e.over?.data.current as CalDropData | undefined;
    let minutes: number | null = null;
    if (d?.kind === 'time') minutes = minutesAt(d);
    stop();
    if (!data || !d) return;
    if (d.kind === 'time') {
      if (minutes !== null) applyDrop(data.taskId, anchor.current, { kind: 'time', day: d.day, minutes });
    } else if (d.kind === 'day') applyDrop(data.taskId, anchor.current, { kind: 'day', day: d.day, allDay: d.allDay });
    else applyDrop(data.taskId, null, { kind: 'unschedule' });
  };

  const activeTask = useDb((db) => (activeTaskId ? db.tasks[activeTaskId] : undefined));
  duration.current = activeTask && isTimedTask(activeTask) ? timedDuration(activeTask) : 30;

  const suppressClick = useCallback(() => Date.now() - endedAt.current < 250, []);
  const value = useMemo(() => ({ activeTaskId, preview, dayRange, suppressClick }), [activeTaskId, preview, dayRange, suppressClick]);

  return (
    <Ctx.Provider value={value}>
      <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={stop}>
        {children}
        <DragOverlay dropAnimation={null}>{activeTaskId ? <DragGhost taskId={activeTaskId} /> : null}</DragOverlay>
      </DndContext>
    </Ctx.Provider>
  );
}

/** Day span a task would occupy if the grabbed day lands on `day`. */
function projectedRange(taskId: string | null, anchorDay: number | null, day: number) {
  const t = taskId ? getDb().tasks[taskId] : undefined;
  if (!t) return null;
  const sp = taskSpan(t);
  if (!sp) return { start: day, end: day };
  const delta = differenceInCalendarDays(day, anchorDay ?? dayOf((t.dueDate ?? t.startDate)!));
  return { start: addDays(sp.start, delta).getTime(), end: addDays(sp.end, delta).getTime() };
}

function DragGhost({ taskId }: { taskId: string }) {
  const task = useDb((db) => db.tasks[taskId]);
  const status = useDb((db) => (task ? statusOf(db, task) : undefined));
  if (!task || !status) return null;
  return (
    <div className="cal-drag-ghost">
      <StatusIcon type={status.type} color={status.color} size={12} />
      <span className="truncate">{task.name}</span>
    </div>
  );
}
